/**
 * <ParentHomePage> — the parent home screen (`/menu/parent`) rebuilt on the DS (page, L0.1).
 *
 * Ordre, par enfant (`DECISIONS.md`, 23/09) : le prénom → « À faire » (seulement s'il y a
 * quelque chose) → « Prochaine séance », seule dans son cadre → les quatre gestes → le stage de
 * vacances en vente (12/10, seulement quand il y en a un pour cet enfant) → « Dernier bilan » →
 * « À venir » (les deux séances suivantes) → « Suivi » → le bandeau de parrainage (28/09,
 * seulement quand le geste existe). À partir de 800 px, « Dernier bilan » et « À venir »
 * se posent côte à côte — l'un des deux absent, l'autre prend la largeur ; tout le reste garde la
 * largeur de la colonne.
 *
 * Pure composition of section templates — no per-section markup here. Data in, callbacks out;
 * the assessment modal's visibility is always controlled by the caller (camera hide does NOT
 * reset the draft). Header, child chips and tab bar belong to the shell, not to this page.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import {
    type UICreateSchoolAssessment,
    type UILiveSession,
    type UIParentLastReport,
    type UIParentStageOffer,
    type UIParentTodo,
    type UIParentRequest,
    type UIRevisionProgram,
    type UISchoolAssessment,
    type UISectionedChapter,
    type UISubject,
} from "../../../ports/primary/ui/types"
import { isPaymentBlocked } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { Avatar } from "../../atoms/Avatar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Title } from "../../atoms/typography/Title"
import { ReferralInviteBanner } from "../../molecules/ReferralInviteBanner"
import { StageOfferBanner } from "../../molecules/StageOfferBanner"
import { type MediaPickerProps } from "../../organisms/MediaPicker"
import { AddSchoolAssessmentModal } from "../../templates/AddSchoolAssessmentModal"
import { FollowUpSection } from "../../templates/FollowUpSection"
import { LastReportSection } from "../../templates/LastReportSection"
import { LiveSessionActionsSheet, type LiveSessionAction } from "../../templates/LiveSessionActionsSheet"
import { NextSessionSection } from "../../templates/NextSessionSection"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { ParentRequestsSection } from "../../templates/ParentRequestsSection"
import { ParentQuickActionsSection } from "../../templates/ParentQuickActionsSection"
import { ParentTodoSection } from "../../templates/ParentTodoSection"
import { SchoolAssessmentDetailModal } from "../../templates/SchoolAssessmentDetailModal"
import { hasUpcomingContent, UpcomingSessionsSection } from "../../templates/UpcomingSessionsSection"
import { ParentHomeSkeleton } from "./ParentHomeSkeleton"

export interface ParentHomePageProps {
    /** The child the home is about — rendered as the page heading when provided. */
    child?: { firstname: string; avatarUrl?: string }

    // ── Quick actions ──
    onMoveSession?: () => void
    onReportLate?: () => void
    onMessageTutor?: () => void
    /**
     * Vient du view-model : sans séance à venir, les gestes d'une séance restent à leur place,
     * inactifs. Défaut `true`.
     */
    hasUpcomingSession?: boolean
    /** « Réserver un cours en plus » — opens the three-step extra-hours purchase. */
    onAddOneOffLesson?: () => void
    addOneOffLessonDisabled?: boolean
    /** Retire la tuile « Réserver un cours en plus » de la rangée. */
    addOneOffLessonHidden?: boolean

    // ── À faire ──
    todos?: UIParentTodo[]
    onTodoPress?: (todo: UIParentTodo) => void

    // ── Demandes en cours ──
    /** Ce que la famille a demandé et attend, une ligne chacune, après la prochaine séance. Vide = pas de bloc. */
    requests?: UIParentRequest[]
    /** Ouvre le détail d'une demande. */
    onOpenRequest?: (request: UIParentRequest) => void

    // ── Prochaine séance · À venir ──
    /**
     * Upcoming sessions, sorted, all days confounded (from the view-model) — the first one goes to
     * « Prochaine séance ».
     */
    nextSessions?: UILiveSession[]
    /** Les lignes de « À venir », déjà coupées par le view-model. */
    upcomingSessions?: UILiveSession[]
    hasMoreLives?: boolean
    /**
     * Le geste choisi dans la feuille d'actions d'une séance, la séance déjà attachée. Toucher le
     * bloc en grand ou une ligne ouvre cette feuille ; l'accueil n'a plus de menu « ⋮ ».
     */
    onSessionAction?: (session: UILiveSession, action: LiveSessionAction) => void
    /**
     * Prénom de l'enfant quand le parent peut basculer sur son espace pour le faire entrer en
     * live : la feuille d'actions y gagne « Rejoindre en tant que … », et la carte de la prochaine
     * séance son bouton « Rejoindre » dans la fenêtre d'entrée. ABSENT = ni ligne, ni bouton.
     */
    joinAsChildFirstname?: string
    onSeeMoreLives?: () => void

    // ── Dernier bilan ──
    lastReport?: UIParentLastReport | null
    onOpenLastReport?: () => void

    // ── Suivi ──
    revisionPrograms: UIRevisionProgram[]
    assessments: UISchoolAssessment[]
    /** La première lecture des contrôles est en route (« Suivi » montre la forme de la ligne). */
    assessmentsLoading?: boolean
    onOpenRevisionProgram?: (program: UIRevisionProgram) => void
    onEditAssessment?: (id: UISchoolAssessment["id"]) => void
    onDeleteAssessment?: (id: UISchoolAssessment["id"]) => void
    /**
     * Accepté et **ignoré** : le détail d'un contrôle s'ouvre maintenant dans sa propre feuille,
     * que la page pose elle-même. Le prop reste le temps que le câblage cesse de le passer.
     */
    onAssessmentDetails?: (id: UISchoolAssessment["id"]) => void

    // ── Stage de vacances ──
    /** Le stage en vente pour cet enfant. ABSENT ou `null` = pas de bandeau. */
    stageOffer?: UIParentStageOffer | null
    /** Ouvre le parcours d'inscription du stage. ABSENT = pas de bandeau non plus. */
    onOpenStageOffer?: () => void

    // ── Parrainage ──
    /** Ouvre la page de parrainage. ABSENT = pas de bandeau (pas d'adresse de tableau de bord). */
    onOpenReferral?: () => void
    /** « 50€ » — le gain du parrain, écrit comme la page. Absent ⇒ le bandeau se passe de montant. */
    referralRewardLabel?: string

    // ── Page state ──
    loading?: boolean
    error?: unknown
    onRetry?: () => void

    /** Controlled assessment modal — always owned by the Screen / story wrapper. */
    assessmentModalVisible: boolean
    onOpenAssessmentModal: () => void
    onCloseAssessmentModal: () => void
    assessmentModalTitle?: string
    isEditingAssessment?: boolean
    createAssessmentData?: UICreateSchoolAssessment
    onCreateAssessmentDataChange?: (next: UICreateSchoolAssessment) => void
    subjects?: UISubject[]
    chapters?: UISectionedChapter[]
    chaptersLoading?: boolean
    mediaProps?: MediaPickerProps
    hideAssessmentModalForCamera?: boolean
    onAnalyzeAssessment?: () => void
    analyzeAssessmentLoading?: boolean
    onSubmitAssessment?: () => void
    submitAssessmentDisabled?: boolean
    submitAssessmentLoading?: boolean

    /**
     * Le clic sur une séance suspendue pour paiement : il ne passe PAS par la feuille d'actions,
     * dont aucune ligne n'aboutirait tant que le paiement n'est pas réglé. ABSENT = la feuille
     * s'ouvre comme pour les autres — elle sera vide.
     */
    onBlockedSessionPress?: (session: UILiveSession) => void

    testID?: string
}

export function ParentHomePage(props: ParentHomePageProps) {
    const {
        child,
        onMoveSession,
        onReportLate,
        onMessageTutor,
        hasUpcomingSession,
        onAddOneOffLesson,
        addOneOffLessonDisabled,
        addOneOffLessonHidden,
        todos = [],
        onTodoPress,
        requests = [],
        onOpenRequest,
        nextSessions = [],
        upcomingSessions = [],
        hasMoreLives,
        onSessionAction,
        joinAsChildFirstname,
        onSeeMoreLives,
        lastReport = null,
        onOpenLastReport,
        revisionPrograms,
        assessments,
        assessmentsLoading = false,
        onOpenRevisionProgram,
        onEditAssessment,
        onDeleteAssessment,
        stageOffer = null,
        onOpenStageOffer,
        onOpenReferral,
        referralRewardLabel,
        loading = false,
        error,
        onRetry,
        assessmentModalVisible,
        onOpenAssessmentModal,
        onCloseAssessmentModal,
        assessmentModalTitle = "Ajouter un contrôle",
        isEditingAssessment = false,
        createAssessmentData,
        onCreateAssessmentDataChange,
        subjects = [],
        chapters,
        chaptersLoading = false,
        mediaProps,
        hideAssessmentModalForCamera = false,
        onAnalyzeAssessment,
        analyzeAssessmentLoading = false,
        onSubmitAssessment,
        submitAssessmentDisabled,
        submitAssessmentLoading = false,
        onBlockedSessionPress,
        testID,
    } = props

    // Quel contrôle et quelle séance sont ouverts : des états de VUE, pas des données — ces deux
    // feuilles ne portent aucun brouillon, contrairement à la modale d'ajout que l'écran possède.
    const [openAssessmentId, setOpenAssessmentId] = React.useState<UISchoolAssessment["id"] | null>(null)
    const openAssessment = assessments.find((item) => item.id === openAssessmentId) ?? null

    const [openSessionId, setOpenSessionId] = React.useState<UILiveSession["id"] | null>(null)
    const openSession = nextSessions.find((item) => item.id === openSessionId) ?? null

    const nextSession = nextSessions[0] ?? null
    const hasUpcoming = hasUpcomingContent(upcomingSessions, hasMoreLives)

    /**
     * Une séance SUSPENDUE n'ouvre pas ses actions — aucune n'aboutirait tant que le paiement
     * n'est pas réglé : le clic repart tel quel à l'appelant, qui propose la régularisation.
     */
    const selectSession = (session: UILiveSession) => {
        if (onBlockedSessionPress != null && isPaymentBlocked(session)) {
            onBlockedSessionPress(session)
            return
        }
        setOpenSessionId(session.id)
    }
    const onSelectSession = onSessionAction != null ? selectSession : undefined
    /** Le bouton de la carte est la ligne de la feuille, sans passer par la feuille : même geste. */
    const onJoinSession =
        onSessionAction != null && joinAsChildFirstname != null
            ? (session: UILiveSession) => onSessionAction(session, "join_as_child")
            : undefined

    /** Modifier / Supprimer quittent la feuille : deux surfaces empilées ne se referment jamais bien. */
    const leaveDetail = (next?: () => void) => () => {
        setOpenAssessmentId(null)
        next?.()
    }

    return (
        <PageTemplate
            testID={testID}
            loading={loading}
            error={error}
            onRetry={onRetry}
            skeleton={<ParentHomeSkeleton />}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            <Stack gap="6">
                {child != null && (
                    <Row gap="3" align="center">
                        <Avatar uri={child.avatarUrl} name={child.firstname} size={48} />
                        <Title size="text-3xl">{child.firstname}</Title>
                    </Row>
                )}

                <ParentTodoSection todos={todos} onTodoPress={onTodoPress} />

                <NextSessionSection
                    session={nextSession}
                    onSelectSession={onSelectSession}
                    onJoinSession={onJoinSession}
                />

                <ParentRequestsSection requests={requests} onOpenRequest={onOpenRequest} />

                <ParentQuickActionsSection
                    onMoveSession={onMoveSession}
                    onReportLate={onReportLate}
                    onMessageTutor={onMessageTutor}
                    hasUpcomingSession={hasUpcomingSession}
                    onAddOneOffLesson={onAddOneOffLesson}
                    addOneOffLessonDisabled={addOneOffLessonDisabled}
                    addOneOffLessonHidden={addOneOffLessonHidden}
                />

                {/* Sous les gestes, pas au-dessus : la prochaine séance et ce qu'on fait d'elle
                    restent ensemble, et l'accueil ne s'ouvre pas sur une vente. */}
                {stageOffer != null && onOpenStageOffer != null && (
                    <StageOfferBanner
                        title={stageOffer.title}
                        datesLabel={stageOffer.datesLabel}
                        childFirstname={child?.firstname}
                        onPress={onOpenStageOffer}
                    />
                )}

                {/* La rangée ne se pose que si l'une des deux a quelque chose : vide, elle
                    compterait un écart de trop dans la pile. « À venir » a quelque chose dès que
                    « Tous mes lives » en a plus que l'accueil, même sans ligne à montrer. */}
                {(lastReport != null || hasUpcoming) && (
                    <View style={styles.pairGrid}>
                        {lastReport != null && (
                            <View style={styles.pairColumn}>
                                <LastReportSection report={lastReport} onOpenReport={onOpenLastReport} />
                            </View>
                        )}
                        {hasUpcoming && (
                            <View style={styles.pairColumn}>
                                <UpcomingSessionsSection
                                    sessions={upcomingSessions}
                                    hasMoreLives={hasMoreLives}
                                    onSeeMoreLives={onSeeMoreLives}
                                    onSelectSession={onSelectSession}
                                />
                            </View>
                        )}
                    </View>
                )}

                <FollowUpSection
                    revisionPrograms={revisionPrograms}
                    assessments={assessments}
                    assessmentsLoading={assessmentsLoading}
                    onOpenRevisionProgram={onOpenRevisionProgram}
                    onAddAssessment={onOpenAssessmentModal}
                    onOpenAssessment={setOpenAssessmentId}
                />

                {onOpenReferral != null && (
                    <ReferralInviteBanner rewardLabel={referralRewardLabel} onPress={onOpenReferral} />
                )}
            </Stack>

            <LiveSessionActionsSheet
                visible={openSession != null}
                onClose={() => setOpenSessionId(null)}
                session={openSession}
                joinAsChildFirstname={joinAsChildFirstname}
                onAction={(action) => {
                    setOpenSessionId(null)
                    if (openSession != null) onSessionAction?.(openSession, action)
                }}
            />

            <SchoolAssessmentDetailModal
                visible={openAssessment != null}
                onClose={leaveDetail()}
                assessment={openAssessment}
                onEdit={onEditAssessment != null ? leaveDetail(() => onEditAssessment(openAssessment!.id)) : undefined}
                onDelete={
                    onDeleteAssessment != null ? leaveDetail(() => onDeleteAssessment(openAssessment!.id)) : undefined
                }
            />

            {createAssessmentData != null && onCreateAssessmentDataChange != null && (
                <AddSchoolAssessmentModal
                    visible={assessmentModalVisible}
                    onClose={onCloseAssessmentModal}
                    title={assessmentModalTitle}
                    isEditing={isEditingAssessment}
                    voice="parent"
                    createAssessmentData={createAssessmentData}
                    onCreateAssessmentDataChange={onCreateAssessmentDataChange}
                    subjects={subjects}
                    chapters={chapters}
                    chaptersLoading={chaptersLoading}
                    mediaProps={mediaProps}
                    hidden={hideAssessmentModalForCamera}
                    onAnalyze={onAnalyzeAssessment}
                    analyzeLoading={analyzeAssessmentLoading}
                    onSubmit={onSubmitAssessment}
                    submitDisabled={submitAssessmentDisabled}
                    submitLoading={submitAssessmentLoading}
                />
            )}
        </PageTemplate>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** « Dernier bilan » et « À venir » : une colonne sur téléphone, côte à côte à partir de 800 px. */
    pairGrid: {
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "flex-start" },
        gap: theme.spacing["6"],
    },
    pairColumn: {
        flexGrow: { xs: 0, md: 1 },
        flexShrink: 1,
        flexBasis: { xs: "auto", md: 0 },
    },
}))
